import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Globe, ChevronDown, CheckCircle2, Menu, X, Phone, Building2 } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { useLanguage } from '../hooks/useLanguage';
import { useCountry } from '../hooks/useCountry';
import { SEO } from '../components/SEO';
type BillingPeriod = 'monthly' | 'yearly';
interface PricingPlan {
  name: string;
  nameAr: string;
  priceMonthly: number;
  priceYearly: number;
  features: string[];
  featuresAr: string[];
  popular?: boolean;
}
export function PricingPage() {
  const {
    language,
    setLanguage
  } = useLanguage();
  const {
    country,
    setCountry,
    currency,
    currencySymbol,
    phoneNumber,
    pricing
  } = useCountry();
  const isRTL = language === 'ar';
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('monthly');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);
  const handleLanguageChange = (newLanguage: 'ar' | 'en') => {
    setLanguage(newLanguage);
    setLanguageDropdownOpen(false);
    window.location.reload();
  };
  const handleCountryChange = (newCountry: 'eg' | 'ae' | 'sa') => {
    setCountry(newCountry);
    setCountryDropdownOpen(false);
    window.location.reload();
  };
  const pricingPlans: PricingPlan[] = [{
    name: 'Starter',
    nameAr: 'المبتدئ',
    priceMonthly: pricing.starter.monthly,
    priceYearly: pricing.starter.yearly,
    features: ['Up to 5,000 Monthly Conversations', 'Limited AI Replies', 'Dashboard', 'Conversations Inbox', 'Limited Follow-up', 'Products & Services', 'Limited Offers Management', 'Limited AI Auto Replies', 'Basic Analytics & Reports', 'Standard Support'],
    featuresAr: ['حتى 5,000 محادثة شهرياً', 'ردود AI محدودة', 'لوحة التحكم', 'صندوق المحادثات', 'متابعة محدودة', 'المنتجات والخدمات', 'إدارة عروض محدودة', 'ردود تلقائية AI محدودة', 'تحليلات وتقارير أساسية', 'دعم قياسي']
  }, {
    name: 'Growth',
    nameAr: 'النمو',
    priceMonthly: pricing.business.monthly,
    priceYearly: pricing.business.yearly,
    features: ['Up to 10,000 Monthly Conversations', 'Standard AI Replies', 'Dashboard', 'Conversations Inbox', 'Full Leads Management', 'Follow-up (Reminders & Auto)', 'Products & Services', 'Offers Management', 'AI Auto Replies', 'Standard Analytics & Reports', 'Priority Support'],
    featuresAr: ['حتى 10,000 محادثة شهرياً', 'ردود AI قياسية', 'لوحة التحكم', 'صندوق المحادثات', 'إدارة عملاء كاملة', 'متابعة (تذكيرات وتلقائية)', 'المنتجات والخدمات', 'إدارة العروض', 'ردود تلقائية AI', 'تحليلات وتقارير قياسية', 'دعم ذو أولوية'],
    popular: true
  }, {
    name: 'Scale',
    nameAr: 'التوسع',
    priceMonthly: pricing.pro.monthly,
    priceYearly: pricing.pro.yearly,
    features: ['Up to 100,000 Monthly Conversations', 'High AI Replies', 'Dashboard', 'Conversations Inbox', 'Advanced Leads Management', 'Follow-up (Reminders & Auto)', 'Products & Services', 'Offers Management', 'AI Auto Replies', 'Advanced Analytics & Reports', 'Priority Support'],
    featuresAr: ['حتى 100,000 محادثة شهرياً', 'ردود AI عالية', 'لوحة التحكم', 'صندوق المحادثات', 'إدارة عملاء متقدمة', 'متابعة (تذكيرات وتلقائية)', 'المنتجات والخدمات', 'إدارة العروض', 'ردود تلقائية AI', 'تحليلات وتقارير متقدمة', 'دعم ذو أولوية']
  }];
  return <div className="min-h-screen bg-white" dir={isRTL ? 'rtl' : 'ltr'} data-id="element-2769">
      <SEO title={isRTL ? 'الأسعار - خطط Operines' : 'Pricing - Operines Plans'} description={isRTL ? 'اختر الخطة المناسبة لعملك. أسعار شفافة وخطط مرنة لأتمتة واتساب بالذكاء الاصطناعي. تجربة مجانية 7 أيام.' : 'Choose the right plan for your business. Transparent pricing and flexible plans for AI-powered WhatsApp automation. 7-day free trial.'} keywords={isRTL ? 'أسعار أتمتة واتساب, خطط واتساب, تسعير بوت واتساب, باقات واتساب بيزنس' : 'WhatsApp automation pricing, WhatsApp plans, chatbot pricing, WhatsApp Business API pricing'} canonicalPath="/pricing" language={language as 'ar' | 'en'} ogType="website" data-id="element-2770" />

      {/* Navigation */}
      <motion.header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-xl border-b border-gray-200 shadow-sm" initial={{
      y: -100
    }} animate={{
      y: 0
    }} data-id="element-2771">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2772">
          <div className="flex items-center justify-between py-4" data-id="element-2773">
            <Link to="/" data-id="element-2774">
              <img src="https://cdn.magicpatterns.com/uploads/8sRwXksp5brxBQY4QcgZ5M/U062aU0635U0645U064aU0645_U0628U062fU0648U0646_U0639U0646U0648U0627U0646_(37).png" alt="Operines" className="h-8 w-auto" data-id="element-2775" />
            </Link>

            <nav className="hidden md:flex items-center gap-8" data-id="element-2776">
              <Link to="/" className="text-gray-600 hover:text-[#4f1964] font-medium transition-colors" data-id="element-2777">
                {isRTL ? 'الرئيسية' : 'Home'}
              </Link>
              <Link to="/about" className="text-gray-600 hover:text-[#4f1964] font-medium transition-colors" data-id="element-2778">
                {isRTL ? 'من نحن' : 'About Us'}
              </Link>
              <Link to="/blog" className="text-gray-600 hover:text-[#4f1964] font-medium transition-colors" data-id="element-2779">
                {isRTL ? 'المدونة' : 'Blog'}
              </Link>
              <Link to="/pricing" className="text-[#4f1964] font-medium transition-colors" data-id="element-2780">
                {isRTL ? 'الأسعار' : 'Pricing'}
              </Link>

              {/* Language Selector */}
              <div className="relative" data-id="element-2781">
                <button onClick={() => setLanguageDropdownOpen(!languageDropdownOpen)} className="flex items-center gap-1 p-2 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-2782">
                  <Globe className="w-5 h-5 text-gray-600" data-id="element-2783" />
                  <ChevronDown className={`w-3 h-3 text-gray-500 transition-transform ${languageDropdownOpen ? 'rotate-180' : ''}`} data-id="element-2784" />
                </button>
                <AnimatePresence data-id="element-2785">
                  {languageDropdownOpen && <motion.div initial={{
                  opacity: 0,
                  y: -10,
                  scale: 0.95
                }} animate={{
                  opacity: 1,
                  y: 0,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  y: -10,
                  scale: 0.95
                }} className="absolute top-full mt-2 right-0 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-50 min-w-[140px]" data-id="element-2786">
                      <button onClick={() => handleLanguageChange('en')} className={`flex items-center gap-3 w-full px-4 py-3 text-sm font-medium transition-colors ${language === 'en' ? 'bg-purple-50 text-[#4f1964]' : 'hover:bg-gray-50 text-gray-700'}`} data-id="element-2787">
                        <span className="text-lg" data-id="element-2788">🇺🇸</span>
                        <span data-id="element-2789">English</span>
                        {language === 'en' && <CheckCircle2 className="w-4 h-4 text-[#4f1964] ml-auto" data-id="element-2790" />}
                      </button>
                      <button onClick={() => handleLanguageChange('ar')} className={`flex items-center gap-3 w-full px-4 py-3 text-sm font-medium transition-colors ${language === 'ar' ? 'bg-purple-50 text-[#4f1964]' : 'hover:bg-gray-50 text-gray-700'}`} data-id="element-2791">
                        <span className="text-lg" data-id="element-2792">🇸🇦</span>
                        <span data-id="element-2793">العربية</span>
                        {language === 'ar' && <CheckCircle2 className="w-4 h-4 text-[#4f1964] ml-auto" data-id="element-2794" />}
                      </button>
                    </motion.div>}
                </AnimatePresence>
              </div>

              {/* Country Selector */}
              <div className="relative" data-id="element-2795">
                <button onClick={() => setCountryDropdownOpen(!countryDropdownOpen)} className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-300 text-sm font-medium hover:bg-gray-50 transition-colors" data-id="element-2796">
                  <span className="text-lg" data-id="element-2797">
                    {country === 'eg' ? '🇪🇬' : country === 'ae' ? '🇦🇪' : '🇸🇦'}
                  </span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${countryDropdownOpen ? 'rotate-180' : ''}`} data-id="element-2798" />
                </button>
                <AnimatePresence data-id="element-2799">
                  {countryDropdownOpen && <motion.div initial={{
                  opacity: 0,
                  y: -10,
                  scale: 0.95
                }} animate={{
                  opacity: 1,
                  y: 0,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  y: -10,
                  scale: 0.95
                }} className="absolute top-full mt-2 right-0 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-50 min-w-[180px]" data-id="element-2800">
                      <button onClick={() => handleCountryChange('eg')} className={`flex items-center gap-3 w-full px-4 py-3 text-sm font-medium transition-colors ${country === 'eg' ? 'bg-purple-50 text-[#4f1964]' : 'hover:bg-gray-50 text-gray-700'}`} data-id="element-2801">
                        <span className="text-2xl" data-id="element-2802">🇪🇬</span>
                        <div className="text-left" data-id="element-2803">
                          <div className="font-semibold" data-id="element-2804">
                            {isRTL ? 'مصر' : 'Egypt'}
                          </div>
                        </div>
                      </button>
                      <button onClick={() => handleCountryChange('ae')} className={`flex items-center gap-3 w-full px-4 py-3 text-sm font-medium transition-colors ${country === 'ae' ? 'bg-purple-50 text-[#4f1964]' : 'hover:bg-gray-50 text-gray-700'}`} data-id="element-2805">
                        <span className="text-2xl" data-id="element-2806">🇦🇪</span>
                        <div className="text-left" data-id="element-2807">
                          <div className="font-semibold" data-id="element-2808">
                            {isRTL ? 'الإمارات' : 'UAE'}
                          </div>
                        </div>
                      </button>
                      <button onClick={() => handleCountryChange('sa')} className={`flex items-center gap-3 w-full px-4 py-3 text-sm font-medium transition-colors ${country === 'sa' ? 'bg-purple-50 text-[#4f1964]' : 'hover:bg-gray-50 text-gray-700'}`} data-id="element-2809">
                        <span className="text-2xl" data-id="element-2810">🇸🇦</span>
                        <div className="text-left" data-id="element-2811">
                          <div className="font-semibold" data-id="element-2812">
                            {isRTL ? 'السعودية' : 'Saudi'}
                          </div>
                        </div>
                      </button>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </nav>

            {/* Mobile Menu Button */}
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2 text-gray-600 hover:text-[#4f1964]" data-id="element-2813">
              {mobileMenuOpen ? <X className="w-6 h-6" data-id="element-2814" /> : <Menu className="w-6 h-6" data-id="element-2815" />}
            </button>

            <div className="hidden md:flex items-center gap-3" data-id="element-2816">
              <Link to="/login" data-id="element-2817">
                <Button variant="outline" size="sm" className="border-gray-300 text-gray-700 hover:bg-gray-50" data-id="element-2818">
                  {isRTL ? 'تسجيل الدخول' : 'Login'}
                </Button>
              </Link>
              <Link to="/register" data-id="element-2819">
                <Button size="sm" className="bg-[#4f1964] hover:bg-[#3d1350] text-white" data-id="element-2820">
                  {isRTL ? 'ابدأ الآن' : 'Start Now'}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-purple-50 to-white" data-id="element-2821">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2822">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-2823">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-purple-100 text-[#4f1964] text-sm font-medium mb-6" data-id="element-2824">
              <span className="relative flex h-2 w-2" data-id="element-2825">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#4f1964] opacity-75" data-id="element-2826"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#4f1964]" data-id="element-2827"></span>
              </span>
              {isRTL ? 'الأسعار' : 'Pricing'}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" data-id="element-2828">
              {isRTL ? 'خطط بسيطة وشفافة' : 'Simple, Transparent Pricing'}
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-2829">
              {isRTL ? 'اختر الباقة المناسبة لحجم عملك. جميع الباقات تشمل تجربة مجانية لمدة 7 أيام' : 'Choose the plan that fits your business size. All plans include a 7-day free trial'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 sm:py-24 bg-gray-50" data-id="element-2830">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2831">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-8 sm:mb-12" data-id="element-2832">
            {/* Billing Toggle */}
            <div className="flex items-center justify-center gap-2 bg-white rounded-lg p-1 border-2 border-gray-200 inline-flex mb-4" data-id="element-2833">
              <button onClick={() => setBillingPeriod('monthly')} className={`px-4 sm:px-6 py-2 rounded-md text-sm font-medium transition-all ${billingPeriod === 'monthly' ? 'bg-[#4f1964] text-white' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-2834">
                {isRTL ? 'شهري' : 'Monthly'}
              </button>
              <button onClick={() => setBillingPeriod('yearly')} className={`px-4 sm:px-6 py-2 rounded-md text-sm font-medium transition-all ${billingPeriod === 'yearly' ? 'bg-[#4f1964] text-white' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-2835">
                {isRTL ? 'سنوي' : 'Yearly'}
              </button>
            </div>

            {billingPeriod === 'yearly' && <div className="inline-block px-4 py-2 bg-green-100 border border-green-200 rounded-full text-green-700 text-sm font-medium" data-id="element-2836">
                ✨{' '}
                {isRTL ? 'وفر أكثر مع الباقة السنوية' : 'Save more with yearly plan'}
              </div>}
          </motion.div>

          <div className="grid md:grid-cols-4 gap-6 sm:gap-8" data-id="element-2837">
            {pricingPlans.map((plan, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className={`relative p-6 sm:p-8 rounded-2xl border-2 transition-all bg-white ${plan.popular ? 'border-[#4f1964] shadow-xl scale-105' : 'border-gray-200 hover:border-gray-300 hover:shadow-lg'}`} data-id="element-2838">
                {plan.popular && <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#4f1964] text-white rounded-full text-sm font-bold" data-id="element-2839">
                    {isRTL ? 'الأكثر شعبية' : 'Most Popular'}
                  </div>}

                <h3 className="text-xl sm:text-2xl font-bold mb-2 text-gray-900" data-id="element-2840">
                  {isRTL ? plan.nameAr : plan.name}
                </h3>
                <div className="mb-6" data-id="element-2841">
                  <div className="flex items-center gap-2 mb-1" data-id="element-2842">
                    <span className="text-sm text-gray-400 line-through" data-id="element-2843">
                      {(billingPeriod === 'monthly' ? plan.priceMonthly * 2 : plan.priceYearly * 2).toLocaleString()}{' '}
                      {currencySymbol}
                    </span>
                    <span className="px-2 py-0.5 bg-red-100 text-red-600 text-xs font-bold rounded-full" data-id="element-2844">
                      -50%
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2" data-id="element-2845">
                    <span className="text-3xl sm:text-4xl font-bold text-gray-900" data-id="element-2846">
                      {billingPeriod === 'monthly' ? plan.priceMonthly.toLocaleString() : plan.priceYearly.toLocaleString()}
                    </span>
                    <span className="text-gray-600" data-id="element-2847">{currencySymbol}</span>
                    <span className="text-gray-500 text-sm" data-id="element-2848">
                      /{' '}
                      {billingPeriod === 'monthly' ? isRTL ? 'شهر' : 'month' : isRTL ? 'سنة' : 'year'}
                    </span>
                  </div>
                </div>

                <ul className="space-y-3 mb-8" data-id="element-2849">
                  {(isRTL ? plan.featuresAr : plan.features).map((feature, j) => <li key={j} className="flex items-center gap-2" data-id="element-2850">
                        <CheckCircle2 className="w-5 h-5 text-[#4f1964] flex-shrink-0" data-id="element-2851" />
                        <span className="text-sm sm:text-base text-gray-700" data-id="element-2852">
                          {feature}
                        </span>
                      </li>)}
                </ul>

                <Link to="/register" data-id="element-2853">
                  <Button fullWidth variant={plan.popular ? 'primary' : 'outline'} className={plan.popular ? 'bg-[#4f1964] hover:bg-[#3d1350]' : 'border-gray-300 text-gray-700 hover:bg-gray-50'} data-id="element-2854">
                    {isRTL ? 'ابدأ الآن' : 'Start Now'}
                  </Button>
                </Link>
              </motion.div>)}

            {/* Custom Plan */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.4
          }} className="relative p-6 sm:p-8 rounded-2xl border-2 border-dashed border-purple-300 bg-gradient-to-br from-purple-50 to-pink-50 hover:border-[#4f1964] hover:shadow-lg transition-all" data-id="element-2855">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full text-sm font-bold" data-id="element-2856">
                {isRTL ? 'للشركات' : 'Enterprise'}
              </div>

              <div className="flex items-center gap-3 mb-4" data-id="element-2857">
                <Building2 className="w-8 h-8 text-[#4f1964]" data-id="element-2858" />
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900" data-id="element-2859">
                  {isRTL ? 'خطة مخصصة' : 'Custom Plan'}
                </h3>
              </div>

              <p className="text-gray-600 mb-6" data-id="element-2860">
                {isRTL ? 'حلول مخصصة للشركات الكبيرة مع دعم مخصص وتكاملات متقدمة' : 'Custom solutions for large enterprises with dedicated support and advanced integrations'}
              </p>

              <ul className="space-y-3 mb-8" data-id="element-2861">
                {[isRTL ? 'محادثات شهرية مخصصة' : 'Custom Monthly Conversations', isRTL ? 'ردود AI مخصصة' : 'Custom AI Replies', isRTL ? 'إدارة عملاء مخصصة' : 'Custom Leads Management', isRTL ? 'تحليلات وتقارير مخصصة' : 'Custom Analytics & Reports', isRTL ? 'دعم مخصص' : 'Dedicated Support', isRTL ? 'مدير حساب مخصص' : 'Dedicated Account Manager'].map((feature, j) => <li key={j} className="flex items-center gap-2" data-id="element-2862">
                    <CheckCircle2 className="w-5 h-5 text-purple-600 flex-shrink-0" data-id="element-2863" />
                    <span className="text-sm sm:text-base text-gray-700" data-id="element-2864">
                      {feature}
                    </span>
                  </li>)}
              </ul>

              <a href={`https://wa.me/${country === 'eg' ? '201508087501' : '971508546970'}`} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-2865">
                <Button size="lg" variant="outline" className="border-green-600 text-green-600 hover:bg-green-50 w-full" data-id="element-2866">
                  <Phone className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'}`} data-id="element-2867" />
                  {isRTL ? 'تواصل واتساب' : 'Contact WhatsApp'}
                </Button>
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#4f1964] text-white py-12" data-id="element-2868">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2869">
          <div className="grid md:grid-cols-3 gap-8 mb-8" data-id="element-2870">
            <div data-id="element-2871">
              <span className="text-2xl font-bold text-white mb-4 block" data-id="element-2872">
                operines
              </span>
              <p className="text-purple-200 text-sm" data-id="element-2873">
                {isRTL ? 'أتمتة واتساب بالذكاء الاصطناعي' : 'AI-Powered WhatsApp Automation'}
              </p>
            </div>
            <div data-id="element-2874">
              <h4 className="font-bold mb-4" data-id="element-2875">
                {isRTL ? 'روابط سريعة' : 'Quick Links'}
              </h4>
              <ul className="space-y-2 text-purple-200 text-sm" data-id="element-2876">
                <li data-id="element-2877">
                  <Link to="/" className="hover:text-white transition-colors" data-id="element-2878">
                    {isRTL ? 'الرئيسية' : 'Home'}
                  </Link>
                </li>
                <li data-id="element-2879">
                  <Link to="/about" className="hover:text-white transition-colors" data-id="element-2880">
                    {isRTL ? 'من نحن' : 'About Us'}
                  </Link>
                </li>
                <li data-id="element-2881">
                  <Link to="/blog" className="hover:text-white transition-colors" data-id="element-2882">
                    {isRTL ? 'المدونة' : 'Blog'}
                  </Link>
                </li>
                <li data-id="element-2883">
                  <Link to="/pricing" className="hover:text-white transition-colors" data-id="element-2884">
                    {isRTL ? 'الأسعار' : 'Pricing'}
                  </Link>
                </li>
                <li data-id="element-2885">
                  <Link to="/privacy" className="hover:text-white transition-colors" data-id="element-2886">
                    {isRTL ? 'سياسة الخصوصية' : 'Privacy Policy'}
                  </Link>
                </li>
                <li data-id="element-2887">
                  <Link to="/terms" className="hover:text-white transition-colors" data-id="element-2888">
                    {isRTL ? 'شروط الخدمة' : 'Terms of Service'}
                  </Link>
                </li>
              </ul>
            </div>
            <div data-id="element-2889">
              <h4 className="font-bold mb-4" data-id="element-2890">
                {isRTL ? 'تواصل معنا' : 'Contact Us'}
              </h4>
              <ul className="space-y-2 text-purple-200 text-sm" data-id="element-2891">
                <li data-id="element-2892">hello@operines.ai</li>
                <li dir="ltr" data-id="element-2893">{phoneNumber}</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-purple-800 pt-8 text-center text-purple-200 text-sm" data-id="element-2894">
            {/* Payment Methods - Stripe Only */}
            <div className="flex items-center justify-center gap-3 mb-4" data-id="element-2895">
              <span className="text-purple-400 text-xs" data-id="element-2896">
                {isRTL ? 'طرق الدفع' : 'Payment Methods'}
              </span>
              <div className="flex items-center gap-2" data-id="element-2897">
                {/* Visa */}
                <div className="bg-white rounded px-1.5 py-0.5 flex items-center justify-center h-7" data-id="element-2898">
                  <svg viewBox="0 0 48 16" className="h-4 w-auto" fill="none" data-id="element-2899">
                    <path d="M17.4 1.2l-3.2 13.6h-2.6l3.2-13.6h2.6zm13.2 8.8l1.4-3.8.8 3.8h-2.2zm2.9 4.8h2.4l-2.1-13.6h-2.2c-.5 0-.9.3-1.1.7l-3.8 12.9h2.7l.5-1.4h3.3l.3 1.4zm-6.6-4.4c0-3.6-5-3.8-5-5.4 0-.5.5-1 1.5-1.1.5-.1 1.9-.1 3.5.6l.6-2.9c-.9-.3-2-.6-3.3-.6-3.5 0-6 1.9-6 4.5 0 2 1.8 3.1 3.1 3.7 1.4.7 1.9 1.1 1.9 1.7 0 .9-1.1 1.3-2.2 1.4-1.8 0-2.9-.5-3.7-.9l-.7 3c.8.4 2.4.7 4 .7 3.7 0 6.1-1.8 6.3-4.7zM12.8 1.2L8.3 14.8H5.5L3.3 3.5c-.1-.5-.3-.7-.7-.9C1.8 2.2.6 1.8 0 1.5l.1-.3h4.3c.5 0 1 .4 1.1 1l1.1 5.7 2.6-6.7h2.6z" fill="#1A1F71" data-id="element-2900" />
                  </svg>
                </div>
                {/* Mastercard */}
                <div className="bg-white rounded px-2 py-1 flex items-center justify-center h-7" data-id="element-2901">
                  <svg viewBox="0 0 32 20" className="h-4 w-auto" fill="none" data-id="element-2902">
                    <circle cx="12" cy="10" r="8" fill="#EB001B" data-id="element-2903" />
                    <circle cx="20" cy="10" r="8" fill="#F79E1B" data-id="element-2904" />
                    <path d="M16 3.8a8 8 0 010 12.4 8 8 0 000-12.4z" fill="#FF5F00" data-id="element-2905" />
                  </svg>
                </div>
              </div>
              <span className="text-purple-500 text-[10px]" data-id="element-2906">
                Powered by{' '}
                <span className="font-bold text-purple-300" data-id="element-2907">Stripe</span>
              </span>
            </div>
            © {new Date().getFullYear()} Operines.{' '}
            {isRTL ? 'جميع الحقوق محفوظة' : 'All rights reserved'}.
          </div>
        </div>
      </footer>
    </div>;
}